/** /notifications/design-imports — notification materials grid + import entry.
 *
 * Materials are durable design-import inputs (a Figma link, an uploaded HTML
 * file — Figma plan §3.6): re-readable, re-prioritized, deprecated and
 * superseded, never deleted. The "Import design" wizard creates one and runs
 * ingest; each ingest lands a Composition Proposal reviewed at
 * /notifications/design-imports/proposals/:id.
 */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Chip, CircularProgress, MenuItem, Tab, Tabs, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as materialsApi from '@/api/materials';
import type { MaterialKind, MaterialListItem } from '@/api/materials';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { operatorActions } from '@/components/ValueActions/identity';
import { act, ValueActions } from '@/components/ValueActions/ValueActions';
import {
  designImportsHref,
  materialDeprecatedActions,
  materialKindActions,
} from '@/features/notifications/notificationLinks';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { makeResourceFiltersAdapter, useGridPresets } from '@/hooks/useGridPresets';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds, tenantIdsToApi } from '@/scope/tenantIds';
import { useTenantScopeList } from '@/scope/tenantScope';

import { ImportDesignWizard } from './ImportDesignWizard';
import { MaterialDetailDialog } from './MaterialDetailDialog';
import { ProposalsPanel } from './ProposalsPanel';

const passthrough = (v: string) => v || undefined;

const FILTERS: FilterDef[] = [
  { param: 'tenant', apiParam: 'tour_operator_id', toApi: tenantIdsToApi, tenantScope: 'multi' },
  { param: 'kind', toApi: passthrough },
  { param: 'deprecated', toApi: passthrough },
];

const PRESET_FILTERS = makeResourceFiltersAdapter(FILTERS);

const DEFAULT_ORDER = [
  'id',
  'tenant',
  'kind',
  'title',
  'priority',
  'source',
  'proposal_count',
  'deprecated',
  'created_ts',
];
const DEFAULT_HIDDEN = ['deprecated'];

const KIND_OPTIONS: MaterialKind[] = ['figma', 'html', 'url', 'note'];

export function MaterialsPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_composition_manage');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detailFor, setDetailFor] = useState<MaterialListItem | null>(null);
  useCreateIntent(() => setWizardOpen(true));
  // ?tab=proposals deep-links the review queue (the ingest watcher's snackbar
  // and help stories point here).
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'proposals' ? 'proposals' : 'materials';
  const setTab = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'proposals') next.set('tab', 'proposals');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  };

  const presets = useGridPresets({
    grid: 'materials',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: DEFAULT_HIDDEN,
    defaultOrdering: '-created_ts',
    filters: PRESET_FILTERS,
  });

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS, presets.filtersLayer);

  // The operators the effective tenant filter names (URL > preset > scope): the
  // picker's value, and what a create dialog pre-selects / features (D9).
  const pageTenants = pageTenantsFrom(getParam('tenant'), useTenantScopeList());
  const kind = getParam('kind') ?? 'all';
  const deprecated = getParam('deprecated') ?? 'false';

  const columns = useMemo<GridColDef<MaterialListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 160,
        sortable: true,
        valueGetter: (_, row) => row.tour_operator.code || row.tour_operator.name,
        renderCell: (p) => (
          // The row click opens the material dialog — the operator's links ride
          // in the cell popover instead.
          <WithTenantMark environment={p.row.tour_operator.environment}>
            <ValueActions
              actions={operatorActions(p.row.tour_operator, {
                href: designImportsHref({ tenant: p.row.tour_operator.id }),
                label: 'Materials of this operator',
              })}
            >
              {p.row.tour_operator.code || p.row.tour_operator.name}
            </ValueActions>
          </WithTenantMark>
        ),
      },
      {
        field: 'kind',
        headerName: 'Kind',
        width: 110,
        renderCell: (p) => (
          <ValueActions chip actions={materialKindActions(p.row.kind)}>
            <Chip size="small" variant="outlined" label={p.row.kind} />
          </ValueActions>
        ),
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        minWidth: 220,
        renderCell: (p) => (
          <Box component="span" sx={{ fontWeight: 500, textDecoration: p.row.deprecated ? 'line-through' : 'none' }}>
            {p.row.title}
          </Box>
        ),
      },
      { field: 'priority', headerName: 'Priority', width: 100 },
      {
        field: 'source',
        headerName: 'Source',
        width: 150,
        sortable: false,
        valueGetter: (_, row) => (row.kind === 'figma' ? row.url : row.has_html ? 'HTML extracted' : ''),
        renderCell: (p) =>
          p.row.kind === 'figma' ? (
            <ValueActions
              chip
              actions={
                p.row.url
                  ? [act.external(p.row.url, 'Open the Figma file'), act.copy(p.row.url, 'Copy URL')]
                  : []
              }
            >
              <Chip
                size="small"
                variant="outlined"
                color={p.row.figma ? 'success' : 'default'}
                label={p.row.figma ? `${p.row.figma.frames.length} frame(s)` : 'not discovered'}
              />
            </ValueActions>
          ) : p.row.has_html ? (
            <Chip size="small" variant="outlined" color="success" label="HTML ready" />
          ) : (
            '—'
          ),
      },
      {
        field: 'proposal_count',
        headerName: 'Proposals',
        width: 100,
        sortable: false,
        valueGetter: (_, row) => row.proposal_count ?? 0,
      },
      {
        field: 'deprecated',
        headerName: 'Deprecated',
        width: 110,
        sortable: false,
        renderCell: (p) =>
          p.row.deprecated ? (
            <ValueActions chip actions={materialDeprecatedActions(true)}>
              <Chip size="small" color="warning" label="deprecated" />
            </ValueActions>
          ) : (
            '—'
          ),
      },
      {
        field: 'created_ts',
        headerName: 'Created',
        width: 170,
        valueGetter: (_, row) => (row.created_ts ? new Date(row.created_ts).toLocaleString() : ''),
      },
    ],
    [],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      materialsApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? '-created_ts',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [orderingFallback],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: pageTenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker
            multiple
            value={pageTenants}
            onChange={(next) => setParam('tenant', formatTenantIds(next.map((t) => t.id)))}
          />
        </Box>
      ),
    },
    {
      key: 'kind',
      priority: 4,
      active: kind !== 'all',
      node: (
        <TextField
          select
          size="small"
          label="Kind"
          value={kind}
          sx={{ width: 140 }}
          onChange={(e) => setParam('kind', e.target.value === 'all' ? null : e.target.value)}
        >
          <MenuItem value="all">Any kind</MenuItem>
          {KIND_OPTIONS.map((k) => (
            <MenuItem key={k} value={k}>
              {k}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'deprecated',
      priority: 3,
      active: deprecated !== 'false',
      node: (
        <TextField
          select
          size="small"
          label="Lifecycle"
          value={deprecated}
          sx={{ width: 150 }}
          onChange={(e) => setParam('deprecated', e.target.value === 'false' ? null : e.target.value)}
        >
          <MenuItem value="false">Active only</MenuItem>
          <MenuItem value="true">Deprecated only</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="design-imports"
        title="Design Imports"
        subtitle="Import client designs (Figma links, HTML files) into reviewable proposals. Nothing goes live from an import: review a proposal, accept its items, apply — the notification is created disabled."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)}>
              Import design
            </Button>
          )
        }
      />

      <ImportDesignWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        pageTenants={pageTenants}
      />
      <MaterialDetailDialog
        material={detailFor}
        onClose={() => setDetailFor(null)}
        canManage={canManage}
      />

      <Tabs value={tab} onChange={(_, v: string) => setTab(v)} sx={{ mb: 1.5 }}>
        <Tab value="materials" label="Materials" />
        <Tab value="proposals" label="Proposals" />
      </Tabs>

      {tab === 'proposals' ? (
        <ProposalsPanel />
      ) : (
        <>
          <ResponsiveFilterBar items={toolbarItems} />

          {presets.blocking ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <ResourceDataGrid<MaterialListItem>
          queryKey={['materials']}
          queryFn={queryFn}
          columns={orderColumns(columns, presets.orderedFields)}
          extra={extra}
          defaultSort={{ field: 'created_ts', sort: 'desc' }}
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={presets.onColumnOrderChange}
          onRowClick={(_id, row) => setDetailFor(row)}
          headerToolbar={{
            onResetAllFilters: resetAllFilters,
            resetDisabled: !anyFilterActive,
            search: true,
            searchPlaceholder: 'Title, description or URL',
            exportFileName: 'design-materials',
            presets: presets.toolbar,
          }}
        />
          )}
        </>
      )}
    </Box>
  );
}
